import { useAllSystemUiConfigs } from 'api/uiConfig/uiConfig.api'
import React, { KeyboardEvent, ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { ResizeableColumn } from 'ui/atoms'
import { useSnapshot } from 'valtio'

import UiConfigPanel from 'views/UiConfigView/components/UiConfigPanel/UiConfigPanel'
import {
  getViewportAnchoredRightSidebarPosition,
  type ViewportAnchoredRightSidebarPosition,
} from './collapsedRightSidebarPosition.helper'
import UiConfigEditor from './components/UiConfigEditor/UiConfigEditor'
import UiConfigPreview, { type PreviewTab } from './components/UiConfigPreview/UiConfigPreview'
import UiConfigRelationsPanel, {
  RELATIONS_PANEL_COLLAPSED_KEY,
} from './components/UiConfigRelationsPanel/UiConfigRelationsPanel'
import UiConfigSaveControls from './components/UiConfigSaveControls/UiConfigSaveControls'
import type { UiConfigSaveControlsHandle } from './components/UiConfigSaveControls/UiConfigSaveControls.types'
import UiConfigTopbarStatus from './components/UiConfigTopbarStatus/UiConfigTopbarStatus'
import styles from './UiConfigView.module.less'
import { useEditorJsonInvalid } from './useEditorJsonInvalid'
import 'ui/uiConfig/components/index'

const COLLAPSED_SIDEBAR_WIDTH = 32
const SPLIT_MODE_MIN_WIDTH = 1200

type CenterMode = 'preview' | 'split' | 'editor'

function useRelationsPanelCollapsedState(): {
  relationsCollapsed: boolean
  handleCollapseRelations: () => void
  handleExpandRelations: () => void
} {
  const [relationsCollapsed, setRelationsCollapsed] = useState<boolean>(
    () => localStorage.getItem(RELATIONS_PANEL_COLLAPSED_KEY) === 'true'
  )

  function handleCollapseRelations(): void {
    setRelationsCollapsed(true)
    localStorage.setItem(RELATIONS_PANEL_COLLAPSED_KEY, 'true')
  }

  function handleExpandRelations(): void {
    setRelationsCollapsed(false)
    localStorage.setItem(RELATIONS_PANEL_COLLAPSED_KEY, 'false')
  }

  return {
    relationsCollapsed,
    handleCollapseRelations,
    handleExpandRelations,
  }
}

function useRightDockViewportPosition(
  viewRef: React.RefObject<HTMLDivElement | null>
): ViewportAnchoredRightSidebarPosition {
  const [rightDockViewportPosition, setRightDockViewportPosition] = useState<ViewportAnchoredRightSidebarPosition>({
    top: 0,
    height: 0,
    right: 0,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: viewRef.current is a DOM ref — mutating it never triggers a re-render, so listing it in deps would be misleading. Empty array is intentional: runs once after mount.
  useLayoutEffect(() => {
    if (!viewRef.current) {
      return
    }

    const viewElement = viewRef.current
    let rafId: number | null = null

    const syncPosition = (): void => {
      if (rafId !== null) {
        return
      }
      rafId = requestAnimationFrame(() => {
        rafId = null
        const rect = viewElement.getBoundingClientRect()
        const nextPosition = getViewportAnchoredRightSidebarPosition(rect, window.innerWidth)

        setRightDockViewportPosition((current) => {
          if (
            current.top === nextPosition.top &&
            current.height === nextPosition.height &&
            current.right === nextPosition.right
          ) {
            return current
          }

          return nextPosition
        })
      })
    }

    syncPosition()

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncPosition)
    observer?.observe(viewElement)

    window.addEventListener('resize', syncPosition)
    window.addEventListener('scroll', syncPosition, { capture: true, passive: true })

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      observer?.disconnect()
      window.removeEventListener('resize', syncPosition)
      window.removeEventListener('scroll', syncPosition, { capture: true })
    }
  }, [])

  return rightDockViewportPosition
}

function useIsCompactViewport(minWidthForSplit: number): boolean {
  const [isCompact, setIsCompact] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }

    return window.innerWidth < minWidthForSplit
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleResize = (): void => {
      setIsCompact(window.innerWidth < minWidthForSplit)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [minWidthForSplit])

  return isCompact
}

function useUiConfigUrlSync(urlUid: number | null): void {
  const snap = useSnapshot(uiConfigStore)
  const editingUid = snap.editingUid
  const nrOfUiConfigs = snap.nrOfUiConfigs
  const navigate = useNavigate()
  const location = useLocation()
  // Guards the store→URL effect against a redundant navigate() on the same render
  // cycle as the initial URL→store sync. Without this, both effects fire on mount
  // when editingUid is already set, causing a spurious history push.
  const isInitialMountRef = useRef(true)

  // biome-ignore lint/correctness/useExhaustiveDependencies: editingUid and navigate intentionally excluded — editingUid would loop with the store→URL effect; navigate is a stable ref
  useEffect(() => {
    if (urlUid === editingUid) {
      return
    }
    if (urlUid == null) {
      uiConfigStore.setEditingUid(null)
      return
    }
    uiConfigStore.setEditingUid(snap.uiConfigs[urlUid] != null ? urlUid : null)
  }, [urlUid])

  // biome-ignore lint/correctness/useExhaustiveDependencies: nrOfUiConfigs is the intentional trigger; urlUid, editingUid, and snap excluded to avoid looping
  useEffect(() => {
    if (urlUid == null || (editingUid === urlUid && snap.uiConfigs[urlUid] != null)) {
      return
    }
    if (snap.uiConfigs[urlUid] != null) {
      uiConfigStore.setEditingUid(urlUid)
    }
    // More configs may still be loading (e.g. base_config arrives before the full list) —
    // wait for the next nrOfUiConfigs increment rather than clearing the URL uid.
  }, [nrOfUiConfigs])

  // biome-ignore lint/correctness/useExhaustiveDependencies: location and navigate intentionally excluded — depending on location would loop on URL changes; navigate is a stable ref. location is read at fire time, and the effect only fires on an editingUid change, so the values are current for that render.
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    if (editingUid == null) {
      return
    }
    const expectedPath = `/uiconfig/${editingUid}`
    if (location.pathname !== expectedPath) {
      // Keep the query string and fragment: selecting another config is navigation within
      // the same route, so anything the URL carries (e.g. the global `?beta` flag that
      // authStore reads on load) must survive it. The comparison stays on pathname alone —
      // location.pathname never contains `?` or `#`.
      navigate(`${expectedPath}${location.search}${location.hash}`)
    }
  }, [editingUid])
}

function useDraftNavigationBlocker(hasDraft: boolean, editingUid: number | null): void {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasDraft && nextLocation.pathname !== currentLocation.pathname
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: editingUid is a synchronous snapshot, current when blocker fires; blocker object is captured via closure and provides proceed/reset when state is 'blocked'
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return
    }
    if (window.confirm('Discard unsaved draft?')) {
      if (editingUid != null) {
        uiConfigStore.clearPreviewDraft(editingUid)
      }
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker.state])
}

export default function UiConfigView(): ReactElement {
  useAllSystemUiConfigs({ mode: `dev` })
  const [componentsCollapsed, setComponentsCollapsed] = useState(false)
  const [activePreviewTab, setActivePreviewTab] = useState<PreviewTab>('preview')
  const [activeCenterMode, setActiveCenterMode] = useState<CenterMode>('preview')
  const [splitPreviewWidthPercent, setSplitPreviewWidthPercent] = useState(50)
  const [isSplitResizing, setIsSplitResizing] = useState(false)
  const snap = useSnapshot(uiConfigStore)
  // Call methods on the snapshot (not the proxy) so that property accesses inside
  // the methods are tracked by Valtio and the component re-renders reactively.
  const hasUnsavedDraft = snap.hasAnyPreviewDraft()

  const editingUid = snap.editingUid
  const selectedUiConfigId = editingUid == null ? null : snap.uiConfigs[editingUid]?.id
  const canEnterJsonEditor = editingUid != null && (typeof selectedUiConfigId !== 'number' || selectedUiConfigId > 0)
  // Template configs have negative IDs; canEnterJsonEditor is false for them, so they never have status state.
  const editingUiConfig = canEnterJsonEditor ? snap.editingUiConfig : null
  const hasDraft = canEnterJsonEditor && editingUid != null ? snap.hasPreviewDraft(editingUid) : false
  const isCompactViewport = useIsCompactViewport(SPLIT_MODE_MIN_WIDTH)
  const viewRef = useRef<HTMLDivElement | null>(null)
  const centerPaneBodyRef = useRef<HTMLDivElement | null>(null)
  const splitOverlayPreviewRef = useRef<HTMLDivElement | null>(null)
  const splitOverlayEditorRef = useRef<HTMLDivElement | null>(null)
  const pendingSplitPreviewWidthPercentRef = useRef<number | null>(null)
  const rightDockViewportPosition = useRightDockViewportPosition(viewRef)
  const { relationsCollapsed, handleCollapseRelations, handleExpandRelations } = useRelationsPanelCollapsedState()

  const { uid: uidParam } = useParams<{ uid?: string }>()
  // Strict regex rejects partial parses ('1abc'), scientific notation ('1e2'), explicitly
  // signed positive values ('+1'), and whitespace. Negative integers are accepted because
  // template UI configs use negative UIDs (e.g. uid: -3 in templateUiConfigs.ts).
  const urlUid = uidParam != null && /^-?\d+$/.test(uidParam) ? parseInt(uidParam, 10) : null
  useUiConfigUrlSync(urlUid)
  useDraftNavigationBlocker(hasDraft, editingUid ?? null)

  useEffect(() => {
    if (!canEnterJsonEditor) {
      setActiveCenterMode('preview')
    }
  }, [canEnterJsonEditor])

  useEffect(() => {
    if (isCompactViewport && activeCenterMode === 'split') {
      setActiveCenterMode('editor')
    }
  }, [isCompactViewport, activeCenterMode])

  const { isEditorJsonInvalid, onJsonValidityChange } = useEditorJsonInvalid(activeCenterMode)

  useEffect(() => {
    if (!isSplitResizing) {
      return
    }

    const previousUserSelect = document.body.style.userSelect
    const previousCursor = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'

    function handleMouseMove(event: MouseEvent): void {
      if (!centerPaneBodyRef.current) {
        return
      }

      const rect = centerPaneBodyRef.current.getBoundingClientRect()
      if (rect.width <= 0) {
        return
      }

      const rawPercent = ((event.clientX - rect.left) / rect.width) * 100
      const boundedPercent = Math.max(25, Math.min(75, rawPercent))
      pendingSplitPreviewWidthPercentRef.current = boundedPercent

      if (splitOverlayPreviewRef.current) {
        splitOverlayPreviewRef.current.style.flexBasis = `${boundedPercent}%`
      }

      if (splitOverlayEditorRef.current) {
        splitOverlayEditorRef.current.style.flexBasis = `${100 - boundedPercent}%`
      }
    }

    function handleMouseUp(): void {
      const pendingWidth = pendingSplitPreviewWidthPercentRef.current

      if (pendingWidth !== null && pendingWidth !== splitPreviewWidthPercent) {
        setSplitPreviewWidthPercent(pendingWidth)
      }

      pendingSplitPreviewWidthPercentRef.current = null
      setIsSplitResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = previousUserSelect
      document.body.style.cursor = previousCursor
    }
  }, [isSplitResizing, splitPreviewWidthPercent])

  function handleChangePreviewTab(nextTab: PreviewTab): void {
    setActivePreviewTab(nextTab)
  }

  function handleCenterModeChange(nextMode: CenterMode): void {
    if (!canEnterJsonEditor && nextMode !== 'preview') {
      return
    }

    if (isCompactViewport && nextMode === 'split') {
      setActiveCenterMode('editor')
      return
    }

    setActiveCenterMode(nextMode)
  }

  function handleSplitDividerKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }

    const nextPercent = event.key === 'ArrowLeft' ? splitPreviewWidthPercent - 5 : splitPreviewWidthPercent + 5
    const boundedPercent = Math.max(25, Math.min(75, nextPercent))
    setSplitPreviewWidthPercent(boundedPercent)
    event.preventDefault()
  }

  useEffect(() => {
    if (!hasUnsavedDraft) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedDraft])

  function handleSplitDividerMouseDown(): void {
    pendingSplitPreviewWidthPercentRef.current = splitPreviewWidthPercent
    setIsSplitResizing(true)
  }

  const effectiveCenterMode = isCompactViewport && activeCenterMode === 'split' ? 'editor' : activeCenterMode
  const splitEditorWidthPercent = 100 - splitPreviewWidthPercent

  const rightSidebarWidth = relationsCollapsed ? COLLAPSED_SIDEBAR_WIDTH : 320

  const saveControlsRef = useRef<UiConfigSaveControlsHandle>(null)

  function handleViewKeyDown(ev: KeyboardEvent<HTMLDivElement>): void {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 's') {
      ev.preventDefault()
      ev.stopPropagation()
      saveControlsRef.current?.triggerSave()
    }
  }

  return (
    <div className={styles.UiConfigView} ref={viewRef} onKeyDown={handleViewKeyDown}>
      <div
        className={styles.UiConfigView_Content}
        style={{
          width: `calc(100% - ${rightSidebarWidth}px)`,
          maxWidth: `calc(100% - ${rightSidebarWidth}px)`,
        }}
      >
        {componentsCollapsed ? (
          <div className={`${styles.UiConfigView_CollapsedBar} ${styles.UiConfigView_CollapsedBar__left}`}>
            <button
              type="button"
              className={`${styles.UiConfigView_CollapsedToggle} ${styles.UiConfigView_CollapsedToggle__left}`}
              onClick={() => setComponentsCollapsed(false)}
              aria-label="Expand components panel"
              title="Expand components panel"
            >
              ▸
            </button>
            <span className={styles.UiConfigView_CollapsedLabel}>Components</span>
          </div>
        ) : null}

        <ResizeableColumn width={componentsCollapsed ? `0px` : `240px`} collapsed={componentsCollapsed}>
          <div
            className={`${styles.UiConfigView_SidePanel} ${
              componentsCollapsed ? styles.UiConfigView_SidePanel__componentsCollapsed : ``
            }`}
            aria-hidden={componentsCollapsed}
          >
            <div className={styles.UiConfigView_SidePanelToolbar} hidden={componentsCollapsed}>
              <span className={styles.UiConfigView_SidePanelTitle}>Components</span>
              <button
                type="button"
                className={`${styles.UiConfigView_SidePanelToggle} ${styles.UiConfigView_SidePanelToggle__left}`}
                onClick={() => setComponentsCollapsed(true)}
                aria-label="Collapse components panel"
                title="Collapse components panel"
              >
                ◂
              </button>
            </div>
            {!componentsCollapsed ? (
              <div className={styles.UiConfigView_SidePanelBody}>
                <UiConfigPanel />
              </div>
            ) : null}
          </div>
        </ResizeableColumn>

        <div className={styles.UiConfigView_CenterPane}>
          <div className={styles.UiConfigView_CenterModeBar}>
            <button
              type="button"
              className={`${styles.UiConfigView_CenterModeButton} ${activeCenterMode === 'preview' ? styles.UiConfigView_CenterModeButton__active : ''}`}
              onClick={() => handleCenterModeChange('preview')}
              aria-pressed={activeCenterMode === 'preview'}
            >
              Focus preview
            </button>
            <button
              type="button"
              className={`${styles.UiConfigView_CenterModeButton} ${activeCenterMode === 'split' ? styles.UiConfigView_CenterModeButton__active : ''}`}
              onClick={() => handleCenterModeChange('split')}
              aria-pressed={activeCenterMode === 'split'}
              disabled={!canEnterJsonEditor}
              title={!canEnterJsonEditor ? 'Template configs are not editable in JSON' : undefined}
            >
              Split
            </button>
            <button
              type="button"
              className={`${styles.UiConfigView_CenterModeButton} ${activeCenterMode === 'editor' ? styles.UiConfigView_CenterModeButton__active : ''}`}
              onClick={() => handleCenterModeChange('editor')}
              aria-pressed={activeCenterMode === 'editor'}
              disabled={!canEnterJsonEditor}
              title={!canEnterJsonEditor ? 'Template configs are not editable in JSON' : undefined}
            >
              Focus JSON
            </button>
            {editingUiConfig ? (
              <>
                <div className={styles.UiConfigView_CenterModeBarActionsGroup}>
                  <UiConfigTopbarStatus isPublished={Boolean(editingUiConfig.is_published)} hasDraft={hasDraft} />
                </div>
              </>
            ) : null}
          </div>

          <div
            className={`${styles.UiConfigView_CenterPaneBody} ${
              effectiveCenterMode === 'split' ? styles.UiConfigView_CenterPaneBody__split : ''
            }`}
            ref={centerPaneBodyRef}
          >
            {effectiveCenterMode !== 'editor' ? (
              <div
                className={styles.UiConfigView_CenterPreviewPane}
                style={
                  effectiveCenterMode === 'split'
                    ? {
                        flexBasis: `${splitPreviewWidthPercent}%`,
                      }
                    : undefined
                }
              >
                <UiConfigPreview activeTab={activePreviewTab} onChangeTab={handleChangePreviewTab} />
              </div>
            ) : null}

            {effectiveCenterMode === 'split' ? (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize split panels"
                aria-valuemin={25}
                aria-valuemax={75}
                aria-valuenow={Math.round(splitPreviewWidthPercent)}
                tabIndex={0}
                className={`${styles.UiConfigView_CenterSplitDivider} ${
                  isSplitResizing ? styles.UiConfigView_CenterSplitDivider__active : ''
                }`}
                onMouseDown={handleSplitDividerMouseDown}
                onKeyDown={handleSplitDividerKeyDown}
                data-testid="center-split-divider"
              />
            ) : null}

            {effectiveCenterMode !== 'preview' ? (
              <div
                className={styles.UiConfigView_CenterEditorPane}
                style={
                  effectiveCenterMode === 'split'
                    ? {
                        flexBasis: `${splitEditorWidthPercent}%`,
                      }
                    : undefined
                }
              >
                <UiConfigEditor onJsonValidityChange={onJsonValidityChange} />
              </div>
            ) : null}

            {effectiveCenterMode === 'split' && isSplitResizing ? (
              <div className={styles.UiConfigView_CenterSplitOverlay} aria-hidden="true">
                <div
                  className={styles.UiConfigView_CenterSplitShadowPane}
                  style={{ flexBasis: `${splitPreviewWidthPercent}%` }}
                  ref={splitOverlayPreviewRef}
                />
                <div className={styles.UiConfigView_CenterSplitDividerOverlay} />
                <div
                  className={styles.UiConfigView_CenterSplitShadowPane}
                  style={{ flexBasis: `${splitEditorWidthPercent}%` }}
                  ref={splitOverlayEditorRef}
                />
              </div>
            ) : null}
          </div>
          <UiConfigSaveControls ref={saveControlsRef} isEditorJsonInvalid={isEditorJsonInvalid} />
        </div>
      </div>

      <div
        className={`${styles.UiConfigView_RightSidebarDock} ${
          relationsCollapsed ? styles.UiConfigView_RightSidebarDock__collapsed : ''
        }`}
        style={{
          top: `${rightDockViewportPosition.top}px`,
          height: `${rightDockViewportPosition.height}px`,
          right: `${rightDockViewportPosition.right}px`,
        }}
      >
        <div className={styles.UiConfigView_SidePanel}>
          <UiConfigRelationsPanel
            collapsed={relationsCollapsed}
            onCollapse={handleCollapseRelations}
            onExpand={handleExpandRelations}
          />
        </div>
      </div>
    </div>
  )
}
